// Share intents — the marketplace-local home for the anchor-styled share
// controls (account-area redesign Task 20 slice 2).
//
// WHY THIS FILE EXISTS: the Email/Text intents are real <a href> elements —
// mailto:/sms: hand off to the customer's own mail/SMS app, which a <button>
// cannot express, and EmbedLink is button-only by design (TD-329). Slice 1
// declared the component locally in ReferPanel.tsx; slice 2 needs the SAME
// visual on the Welcome tab's share-link block, which makes three surfaces
// (refer form, Welcome block, and the TD-593-filed future primitives-tier
// promotion). Hoisted here verbatim — no restyling, no new primitive (the
// primitives-tier promotion is FILE-ONLY tech debt, not built in this slice;
// the first-class-shared-infra ruling).
//
// S2(i) posture carries over: every consumer of these helpers is a pure
// CLIENT-SIDE intent — clipboard write, mailto:, sms:, navigator.share.
// Aldelo never sends anything; nothing here touches the network layer.
//
// Inline styles only — no Tailwind (embed convention). This module renders an
// <a>, never a <button>, so project rule 73's explicit-type duty does not
// arise here.

import { useState, type CSSProperties } from "react";
import { useEmbedTokens } from "../../embed-shared/primitives";
import { useReducedMotion, motionSafe } from "../../embed-shared/theme";

/** The mailto subject — an operator-review draft (see the task report). */
export const REFER_SHARE_EMAIL_SUBJECT = "A referral for your store";

/** mailto: with the subject + the percent-encoded body (RFC 6068: the
 *  recipient stays empty — the customer fills it in their mail client). */
export function mailtoHref(message: string): string {
  return `mailto:?subject=${encodeURIComponent(REFER_SHARE_EMAIL_SUBJECT)}&body=${encodeURIComponent(message)}`;
}

/**
 * sms: with the percent-encoded body. `sms:?body=` is the broadly compatible
 * form (modern iOS and Android both honour it); no recipient is preset.
 */
export function smsHref(message: string): string {
  return `sms:?body=${encodeURIComponent(message)}`;
}

/**
 * The Email/Text intents are real <a href> elements — see the file header for
 * why (TD-329; a <button> cannot express a mailto:/sms: handoff). The style
 * mirrors EmbedButton's secondary/sm exactly (the StoreBands.tsx precedent:
 * one visual, two elements).
 */
export function ShareIntentLink({ href, children }: { href: string; children: string }) {
  const t = useEmbedTokens();
  const reduced = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "inherit",
    fontSize: t.fontSize.xs,
    fontWeight: t.fontWeight.medium,
    height: t.pointer === "fine" ? Math.max(t.size.control - 8, 30) : t.buttonHeight,
    padding: "0 12px",
    borderRadius: t.borderRadiusControl,
    border: `1px solid ${t.colors.border}`,
    background: hovered ? t.colors.surfaceHover : t.colors.surface,
    color: t.colors.text,
    textDecoration: "none",
    cursor: "pointer",
    // The EmbedButton hover transition (EmbedButton.tsx) — without it Email/
    // Text snap on hover while the adjacent Copy/Share buttons fade.
    transition: motionSafe(reduced, "background .12s,border-color .12s,color .12s"),
  };
  return (
    <a href={href} style={style} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {children}
    </a>
  );
}
